<script setup lang="ts">
import type { Editor } from '@tiptap/vue-3'
defineOptions({
  inheritAttrs: false
})

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    editor?: Editor | null
    type: 'color' | 'highlight'
    modelValue: string
    colorPresets?: { color: string; label: string }[]
  }>(),
  {
    colorPresets: () => []
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'clear'): void
}>()

const showColorPicker = ref(false)

const colorModel = computed({
  get: () => props.modelValue.replace('#', ''),
  set: (value: string) => emit('update:modelValue', value)
})

const clearColor = () => {
  emit('clear')
}
</script>

<template>
  <button
    type="button"
    class="flex min-h-8 items-center justify-center rounded px-1.5 py-1 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
    :class="{
      'bg-stone-200 text-foreground':
        type === 'highlight' ? editor?.isActive('highlight') : editor?.isActive('textStyle')
    }"
    :title="
      type === 'highlight'
        ? t('pages.email.emailTemplateCreatePage.toolbar.highlightColor')
        : t('pages.email.emailTemplateCreatePage.toolbar.textColor')
    "
    @click="showColorPicker = !showColorPicker"
  >
    <template v-if="type === 'highlight'">
      <div
        class="h-4 w-4 rounded ring-1 ring-inset ring-black/10"
        :style="{ backgroundColor: modelValue ? `#${modelValue}` : '#fff' }"
      />
    </template>
    <template v-else>
      <div class="flex flex-col items-center">
        <BaseIcon name="IconoirText" class="h-4 w-4" />
        <div class="mt-px h-[2px] w-3.5 rounded-sm" :style="{ backgroundColor: `#${modelValue}` }" />
      </div>
    </template>
    <BaseIcon name="IconoirNavArrowDown" class="h-3 w-3 ml-0.5" />
  </button>

  <div
    v-if="showColorPicker"
    class="absolute left-0 top-full z-[60] mt-1 rounded-xl border border-border bg-white p-3 shadow-md"
  >
    <BaseColorPicker
      v-model="colorModel"
      :presets="colorPresets"
      :show-clear="type === 'highlight'"
      @clear="clearColor"
    />
  </div>
</template>
